// Remembering WHICH FILE a build is, so it need not be picked again.
//
// ── The problem this solves ────────────────────────────────────────────────
// `HexFileLocalLocation` records where a build came from —
// `H:/Mix_Data/espressif/claude/OtaForSwitching/OtaForSwitching.ino.bin` — but
// it is a STRING, and no web page can open a file from a string. See the note
// above `pickBinFile` in otaBinFiles.js for why that boundary exists and is not
// going away. So publishing meant navigating the picker every single time, and
// picking the wrong build out of a folder of near-identical names is exactly
// the mistake that is expensive here: the upload IS the deployment.
//
// ── What CAN be kept ───────────────────────────────────────────────────────
// A `FileSystemFileHandle` is structured-cloneable, so it survives in
// IndexedDB. Keep one per bin file name and the file can be re-read later with
// no picker at all — and re-read means re-read: a rebuild at the same path is
// picked up automatically, because the handle points at the file, not at a copy
// of its bytes.
//
// ⚠️ THE HANDLE IS NOT A PERMISSION. Chrome grants read access for the session;
// after a browser restart `queryPermission` typically answers `'prompt'` again,
// and re-granting REQUIRES A USER GESTURE. So callers must handle three states,
// not two: granted (just read it), prompt (one click to restore), and gone.
// Treating 'prompt' as failure would send the operator back to the picker,
// which is the thing being removed.
//
// ⚠️ SECURE CONTEXT ONLY, so none of this exists on the deployed plain-HTTP
// site — the same wall that killed Web Bluetooth (core/ble/bleClient.js). It is
// a progressive enhancement over the hidden `<input type=file>`, never a
// replacement, and every caller keeps the manual path working.
//
// Its own tiny database on purpose: core/db/idb.js is the offline-sync store
// with a fixed schema and a version, and adding an object store to it would put
// a firmware convenience into the migration path of the device cache.

const DB_NAME = 'sli-build-handles';
const DB_VERSION = 1;
const STORE = 'handles';

export const HANDLE_UNSUPPORTED = 'unsupported';
export const HANDLE_NONE = 'none';
export const HANDLE_PROMPT = 'prompt';
export const HANDLE_GRANTED = 'granted';
export const HANDLE_MISSING = 'missing';

export const supportsHandles = () => typeof window !== 'undefined'
  && typeof window.indexedDB !== 'undefined'
  && typeof window.showOpenFilePicker === 'function';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const store = (db, mode) => db.transaction(STORE, mode).objectStore(STORE);

export async function saveHandle(binFileName, handle) {
  if (!supportsHandles() || !binFileName || !handle) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const r = store(db, 'readwrite').put(handle, String(binFileName));
      r.onsuccess = resolve;
      r.onerror = () => reject(r.error);
    });
    db.close();
  } catch (e) { /* a remembered handle is a convenience, never a requirement */ }
}

export async function loadHandle(binFileName) {
  if (!supportsHandles() || !binFileName) return null;
  try {
    const db = await openDb();
    const handle = await new Promise((resolve, reject) => {
      const r = store(db, 'readonly').get(String(binFileName));
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => reject(r.error);
    });
    db.close();
    return handle;
  } catch (e) {
    return null;
  }
}

export async function forgetHandle(binFileName) {
  if (!supportsHandles() || !binFileName) return;
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const r = store(db, 'readwrite').delete(String(binFileName));
      r.onsuccess = resolve;
      r.onerror = resolve;
    });
    db.close();
  } catch (e) { /* ignore */ }
}

/**
 * What can we do with the remembered build for this name, right now?
 *
 * -> { state, handle, file, name }
 *
 *    HANDLE_GRANTED      `file` is read and ready — no picker, no click
 *    HANDLE_PROMPT       the handle is kept but access needs one gesture
 *    HANDLE_MISSING      the handle is kept and the file is gone (moved/renamed)
 *    HANDLE_NONE         nothing remembered for this name yet
 *    HANDLE_UNSUPPORTED  this browser cannot do it at all
 *
 * ⚠️ NEVER CALLS requestPermission. That needs a user gesture, so calling it
 * from an effect fails — and fails by leaving the UI claiming the file is
 * unavailable when one click would have had it.
 */
export async function inspectRemembered(binFileName) {
  if (!supportsHandles()) return { state: HANDLE_UNSUPPORTED };
  const handle = await loadHandle(binFileName);
  if (!handle) return { state: HANDLE_NONE };

  let perm = 'prompt';
  try {
    perm = await handle.queryPermission({ mode: 'read' });
  } catch (e) {
    perm = 'prompt';
  }
  if (perm !== 'granted') return { state: HANDLE_PROMPT, handle, name: handle.name };

  try {
    const file = await handle.getFile();
    return { state: HANDLE_GRANTED, handle, file, name: file.name };
  } catch (e) {
    // Granted, but getFile threw — the file was moved, renamed or deleted.
    return { state: HANDLE_MISSING, handle, name: handle.name };
  }
}

/**
 * Restore access after a restart. MUST be called from a click.
 *
 * -> the File, or null if the operator declined or the file has gone.
 */
export async function regrantAndRead(handle) {
  if (!handle) return null;
  try {
    const perm = await handle.requestPermission({ mode: 'read' });
    if (perm !== 'granted') return null;
    return await handle.getFile();
  } catch (e) {
    return null;
  }
}
